import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MetaAPI, type WhatsAppSignupPayload } from "../api/meta";
import { connectorKeys } from "./use-connectors";

export const metaKeys = {
  settings: (clientId: string) => ["meta", "settings", clientId] as const,
  otp: (clientId: string, number: string) =>
    ["meta", "otp", clientId, number] as const,
};

export function useMetaSettings(clientId: string) {
  return useQuery({
    queryKey: metaKeys.settings(clientId),
    queryFn: () => MetaAPI.getSettings(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

const useRefreshConnectors = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: connectorKeys.lists() });
};

export function useConnectWhatsApp() {
  const refresh = useRefreshConnectors();
  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: WhatsAppSignupPayload;
    }) => MetaAPI.connectWhatsApp(clientId, data),
    onSuccess: refresh,
  });
}

export function useConnectMetaPage() {
  const refresh = useRefreshConnectors();
  return useMutation({
    mutationFn: ({
      clientId,
      ...data
    }: {
      clientId: string;
      handle: string;
      page_id: string;
      channel: "messenger" | "instagram";
      agent_id?: string;
    }) => MetaAPI.connectPage(clientId, data),
    onSuccess: refresh,
  });
}

/**
 * The code Meta sends to a number nobody can read. Looks every 10 seconds while
 * `listening` until a code newer than `after` is there; each look keeps the
 * line armed on the server, and the server only reads a call again when it
 * has something new in it.
 */
export function useMetaOtp(
  clientId: string,
  number: string,
  listening: boolean,
  after = 0,
) {
  return useQuery({
    queryKey: [...metaKeys.otp(clientId, number), after] as const,
    queryFn: () => MetaAPI.checkOtp(clientId, number, after),
    enabled: !!clientId && !!number && listening,
    refetchInterval: (query) => (query.state.data?.otp ? false : 10_000),
    retry: false,
  });
}
